import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminActivityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = location.state?.currentUser || getAdminSession();
  
  const [activityData, setActivityData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check if user is superadmin
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'superadmin') {
      toast.error('Access denied. Super admin only.');
      navigate('/admin/waitlist');
      return;
    }
    fetchActivity();
  }, [currentUser, navigate]);
  
  const fetchActivity = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity`);
      setActivityData(response.data);
    } catch (error) {
      toast.error('Failed to fetch activity log');
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const filteredData = activityData.filter(item => 
    item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (!currentUser) return null;
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk'] mb-2">Admin Activity Log</h1>
            <p className="text-muted-foreground">Login history and access tracking</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/waitlist', { state: { currentUser } })}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Activity Table */}
        <Card className="bg-card border-border/50 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-foreground">Email</TableHead>
                  <TableHead className="text-foreground">Name</TableHead>
                  <TableHead className="text-foreground">Role</TableHead>
                  <TableHead className="text-foreground">Login Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No activity found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => (
                    <TableRow key={index} className="border-border/50">
                      <TableCell className="font-medium text-foreground">{item.email}</TableCell>
                      <TableCell className="text-foreground">{item.name}</TableCell>
                      <TableCell>
                        <Badge className={item.role === 'superadmin' ? 'bg-accent/20 text-accent border-accent/30' : 'bg-primary/20 text-primary border-primary/30'}>
                          {item.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(item.login_time).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
        
        <div className="mt-4 text-sm text-muted-foreground">
          Total logins: {filteredData.length}
        </div>
      </div>
    </div>
  );
}